import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Eye, EyeOff, Lock, Mail, CheckCircle, XCircle } from "lucide-react";
import { useAuthStore, AuthUser } from "../stores/authStore";
import toast from "react-hot-toast";
import { ACTION_KEYS } from "../constants/actions";

interface SetPasswordForm {
  password: string;
  confirmPassword: string;
}

function getDefaultRoute(permissions: string[]): string {
  if (permissions.includes(ACTION_KEYS.DASHBOARD_VIEW)) return "/";
  if (permissions.includes(ACTION_KEYS.CAPTURE_RATE_VIEW)) return "/capture-rates";
  if (permissions.includes(ACTION_KEYS.BILLING_VIEW)) return "/billing-jobs";
  if (permissions.includes(ACTION_KEYS.CUSTOMER_VIEW)) return "/customer-profiles";
  return "/clock";
}

const API_BASE = import.meta.env.VITE_API_URL || "";

export function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const token = searchParams.get("token") ?? "";

  const [tokenState, setTokenState] = useState<"loading" | "valid" | "invalid">("loading");
  const [inviteEmail, setInviteEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SetPasswordForm>();

  useEffect(() => {
    if (!token) {
      setTokenState("invalid");
      return;
    }
    fetch(`${API_BASE}/api/v1/auth/invite-info?token=${encodeURIComponent(token)}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.valid) {
          setInviteEmail(data.email);
          setTokenState("valid");
        } else {
          setTokenState("invalid");
        }
      })
      .catch(() => setTokenState("invalid"));
  }, [token]);

  const onSubmit = async (data: SetPasswordForm) => {
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/accept-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, password: data.password }),
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(payload.message || "Failed to set password.");
      }

      const user: AuthUser = payload.user;
      setUser(user);
      toast.success("Account created! Welcome.");
      navigate(getDefaultRoute(user.permissions ?? []));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center">
            <Lock className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Set your password
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Complete your account setup for NR Medical Portal
          </p>
        </div>

        {tokenState === "loading" && (
          <div className="text-center py-8 text-gray-500">Verifying invite link…</div>
        )}

        {tokenState === "invalid" && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
            <XCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
            <p className="font-medium text-red-700">This invite link is invalid or has expired.</p>
            <p className="mt-1 text-sm text-red-600">
              Please ask your administrator to send a new invite.
            </p>
          </div>
        )}

        {tokenState === "valid" && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* Read-only email */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Your login email
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <input
                  type="email"
                  value={inviteEmail}
                  readOnly
                  className="input pl-10 pr-10 bg-gray-100 cursor-not-allowed text-gray-500"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register("password", {
                    required: "Password is required",
                    minLength: { value: 8, message: "Password must be at least 8 characters" },
                    maxLength: { value: 128, message: "Password must be under 128 characters" },
                  })}
                  type={showPassword ? "text" : "password"}
                  className="input pl-10 pr-10"
                  placeholder="Choose a password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-danger-600">{errors.password.message}</p>
              )}
            </div>

            {/* Confirm password */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Confirm password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register("confirmPassword", {
                    required: "Please confirm your password",
                    validate: (v) => v === watch("password") || "Passwords do not match",
                  })}
                  type={showConfirm ? "text" : "password"}
                  className="input pl-10 pr-10"
                  placeholder="Repeat your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirm(!showConfirm)}
                >
                  {showConfirm ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-danger-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary btn-lg w-full"
            >
              {isSubmitting ? "Setting up account…" : "Create account & sign in"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
